import React, { useRef, useState, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import useCloseOnScroll from './useCloseOnScroll';

/**
 * A wrapper around Material UI's Tooltip component that automatically closes
 * the tooltip when any scroll/wheel/touchmove event occurs on the parent scrollable elements.
 */
export const AppTooltip = ({ children, title, open: controlledOpen, onOpen, onClose, ...props }) => {
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback((event) => {
    setIsOpen(false);
    if (onClose) onClose(event);
  }, [onClose]);

  useCloseOnScroll(anchorRef, handleClose);

  const handleOpen = useCallback((event) => {
    setIsOpen(true);
    if (onOpen) onOpen(event);
  }, [onOpen]);

  const isTooltipOpen = controlledOpen !== undefined ? controlledOpen : isOpen;

  // Clone child to attach ref if it's a valid React element
  const renderedChild = React.isValidElement(children) ? (
    React.cloneElement(children, {
      ref: (node) => {
        // Handle both our internal ref and any ref passed to the child element
        anchorRef.current = node;
        const ref = children.props?.ref || children.ref;
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else if ('current' in ref) {
            ref.current = node;
          }
        }
      }
    })
  ) : (
    children
  );

  return (
    <Tooltip
      {...props}
      open={isTooltipOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      title={title}
    >
      {renderedChild}
    </Tooltip>
  );
};

export default AppTooltip;
